/**
 * Citron product carousel (sections/citron-product-carousel.liquid) and its
 * floating cards (snippets/citron-float-card.liquid).
 *
 * Vanilla, no dependencies, loaded with `defer`. Everything is delegated from
 * `document`, so any number of carousels on a page share one set of listeners
 * and markup re-rendered by the theme editor needs no re-initialising.
 *
 *  - Arrows scroll the native scroll-snap track by one page of cards.
 *  - Swatches swap the card's image, price, compare-at price, % chip and the
 *    variant Buy Now adds.
 *  - Buy Now posts to /cart/add.js, then goes to checkout or opens the cart
 *    drawer (section setting). Errors show in a toast.
 *  - CitronCarousel.track: the scroll-snap maths, shared with the collection
 *    showcase (citron-showcase.js) so there is one slider implementation.
 */
(() => {
  if (window.CitronCarousel) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const variantCache = new WeakMap();

  /* ------------------------------------------------------------------ */
  /* Shared scroll-track helpers (also used by citron-showcase.js)       */
  /* ------------------------------------------------------------------ */

  // Maths for a native scroll-snap row. Items are measured with offsetLeft,
  // so the track must be positioned, and transforms on items are ignored.
  const trackHelpers = {
    // The scrollLeft that puts `item` at the track's snap position (or at
    // `align`, for rows without snapping).
    offsetFor(track, item, align = getComputedStyle(item).scrollSnapAlign.split(' ').pop()) {
      if (align === 'center') return item.offsetLeft + item.offsetWidth / 2 - track.clientWidth / 2;
      return item.offsetLeft - (parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0);
    },

    // Index of the item closest to the snap position right now.
    nearest(track, items) {
      let best = 0;
      let bestDistance = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(trackHelpers.offsetFor(track, item) - track.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    },

    go(track, item, smooth = true, align) {
      track.scrollTo({ left: trackHelpers.offsetFor(track, item, align), behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
    },

    edges(track) {
      const max = track.scrollWidth - track.clientWidth;
      return { start: track.scrollLeft <= 2, end: track.scrollLeft >= max - 2, overflow: max > 2 };
    },
  };

  /* ------------------------------------------------------------------ */
  /* Track + arrows                                                      */
  /* ------------------------------------------------------------------ */

  function pageStep(track) {
    const item = track.firstElementChild;
    if (!item) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item.getBoundingClientRect().width + gap;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    return step * perPage;
  }

  function scrollTrack(button) {
    const carousel = button.closest('[data-ctr-carousel]');
    const track = carousel && carousel.querySelector('[data-ctr-track]');
    if (!track) return;

    const dir = button.hasAttribute('data-ctr-prev') ? -1 : 1;
    track.scrollBy({ left: dir * pageStep(track), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  function syncArrows(track) {
    const carousel = track.closest('[data-ctr-carousel]');
    if (!carousel) return;

    const edges = trackHelpers.edges(track);
    const prev = carousel.querySelector('[data-ctr-prev]');
    const next = carousel.querySelector('[data-ctr-next]');
    if (prev) prev.disabled = edges.start;
    if (next) next.disabled = edges.end;
    carousel.classList.toggle('ctr-pc--no-overflow', !edges.overflow);
  }

  let scrollQueued = new Set();
  let scrollFrame = null;

  // `scroll` does not bubble, so listen in the capture phase.
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof Element) || !track.matches('[data-ctr-track]')) return;
      scrollQueued.add(track);
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollQueued.forEach(syncArrows);
        scrollQueued = new Set();
        scrollFrame = null;
      });
    },
    { capture: true, passive: true }
  );

  function syncAll() {
    document.querySelectorAll('[data-ctr-track]').forEach(syncArrows);
  }

  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncAll, 150);
    },
    { passive: true }
  );

  /* ------------------------------------------------------------------ */
  /* Swatches                                                            */
  /* ------------------------------------------------------------------ */

  function variantsFor(card) {
    if (variantCache.has(card)) return variantCache.get(card);
    let list = [];
    try {
      list = JSON.parse(card.querySelector('[data-ctr-card-variants]').textContent);
    } catch (e) {
      list = [];
    }
    variantCache.set(card, list);
    return list;
  }

  function selectSwatch(swatch) {
    const card = swatch.closest('[data-ctr-card]');
    if (!card) return;

    const id = String(swatch.dataset.variantId);
    const variant = variantsFor(card).find((v) => String(v.id) === id);
    if (!variant) return;

    card.querySelectorAll('[data-ctr-swatch]').forEach((sw) => {
      sw.setAttribute('aria-pressed', sw === swatch ? 'true' : 'false');
    });

    const img = card.querySelector('.ctr-fc__img');
    if (img && img.tagName === 'IMG' && variant.src) {
      if (variant.srcset) img.srcset = variant.srcset;
      img.src = variant.src;
    }

    const price = card.querySelector('[data-ctr-card-price]');
    if (price) price.textContent = variant.price;

    const compare = card.querySelector('[data-ctr-card-compare]');
    const compareValue = card.querySelector('[data-ctr-card-compare-value]');
    if (compare) compare.hidden = !variant.compare;
    if (compareValue && variant.compare) compareValue.textContent = variant.compare;

    const chip = card.querySelector('[data-ctr-card-chip]');
    if (chip) {
      chip.textContent = `${variant.off}% OFF`;
      chip.hidden = !(variant.off > 0);
    }

    const buy = card.querySelector('[data-ctr-buy]');
    if (buy) {
      buy.dataset.variantId = variant.id;
      buy.disabled = !variant.available;
      const label = buy.querySelector('[data-ctr-buy-label]');
      if (label) label.textContent = variant.available ? buy.dataset.label : buy.dataset.labelSoldout;
    }

    card.classList.toggle('is-soldout', !variant.available);
  }

  /* ------------------------------------------------------------------ */
  /* Buy Now                                                             */
  /* ------------------------------------------------------------------ */

  async function buyNow(button) {
    const id = Number(button.dataset.variantId);
    if (!id || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    const done = () => {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    };

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'This watch could not be added to your cart.');
      }

      if (button.dataset.mode === 'checkout') {
        // Stay in the loading state while the browser navigates away.
        window.location.href = `${root}checkout`;
        return;
      }

      const cart = await fetch(`${root}cart.js`)
        .then((r) => r.json())
        .catch(() => null);

      // The same event Horizon's cart icon, cart drawer and header listen for
      // (matches citron-rp-card in citron-product.js).
      const event = new Event('cart:update', { bubbles: true });
      event.detail = {
        resource: cart,
        sourceId: 'citron-carousel',
        data: { source: 'citron-carousel', itemCount: cart ? cart.item_count : undefined },
      };
      document.dispatchEvent(event);

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      done();
    } catch (error) {
      done();
      toast(error.message || 'Something went wrong. Please try again.');
    }
  }

  let toastEl;
  let toastTimer;

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ctr-toast';
      toastEl.setAttribute('role', 'alert');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // Next frame, so the transition runs even on the first toast.
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 4500);
  }

  /* ------------------------------------------------------------------ */
  /* One delegated click handler                                         */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const arrow = target.closest('[data-ctr-prev], [data-ctr-next]');
    if (arrow) {
      scrollTrack(arrow);
      return;
    }

    const swatch = target.closest('[data-ctr-swatch]');
    if (swatch) {
      event.preventDefault();
      selectSwatch(swatch);
      return;
    }

    const buy = target.closest('[data-ctr-buy]');
    if (buy) {
      event.preventDefault();
      buyNow(buy);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAll);
  else syncAll();
  document.addEventListener('shopify:section:load', syncAll);

  window.CitronCarousel = { toast, track: trackHelpers };
})();
